import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { TaskExportService } from '@/domains/task/services/task-export-service';
import { DesignFtoApi, type ExportReportResponse } from '@/presentation/pages/design-fto/apis/design-fto-api';
import {
    DESIGN_FTO_EXPORT_MAX_ATTEMPTS,
    DESIGN_FTO_EXPORT_POLL_INTERVAL,
} from '../../constants/work-in-progress';
import { AiTaskType, type WorkInProgressTask } from '../../types/work-in-progress';
import type { WorkInProgressExportService } from '../work-in-progress-export-service';
import { WorkInProgressFtoExportService } from '../work-in-progress-fto-export-service';

/**
 * 根据任务类型分派至现有业务接口的报告导出实现。
 */
@injectable()
export class WorkInProgressExportServiceImpl implements WorkInProgressExportService {
    constructor(
        @inject(TaskExportService)
        private readonly _taskExportService: TaskExportService,
        @inject(WorkInProgressFtoExportService)
        private readonly _ftoExportService: WorkInProgressFtoExportService,
        @inject(DesignFtoApi)
        private readonly _designFtoApi: DesignFtoApi,
    ) {}

    async exportReport(task: WorkInProgressTask): Promise<string> {
        const downloadUrl = await this._getDownloadUrl(task);
        if (!downloadUrl) {
            throw new Error('Export did not return a download URL');
        }
        return downloadUrl;
    }

    private async _getDownloadUrl(task: WorkInProgressTask): Promise<string> {
        if (task.task_type === AiTaskType.AI_NOVELTY_SEARCH) {
            const result = await this._taskExportService.exportTask({
                taskId: task.task_id,
                format: 'WORD',
            });
            return result.url;
        }
        if (task.task_type === AiTaskType.AI_NEW_FTO_SEARCH) {
            return this._ftoExportService.exportReport(task.task_id);
        }
        if (task.task_type === AiTaskType.AI_IMAGE_SEARCH) {
            return this._exportDesignFtoReport(task.task_id);
        }
        throw new Error('AI_NOVELTY_SEARCH_V2 export is not supported');
    }

    private async _exportDesignFtoReport(taskId: string): Promise<string> {
        let result = await this._designFtoApi.exportReport(taskId);
        for (let attempt = 0; attempt < DESIGN_FTO_EXPORT_MAX_ATTEMPTS; attempt += 1) {
            const downloadUrl = this._getCompletedDesignExportUrl(result);
            if (downloadUrl) return downloadUrl;
            const statusUrl = this._getDesignExportStatusUrl(taskId, result);
            await this._waitForNextDesignExportPoll();
            result = await this._designFtoApi.getExportReportStatus(statusUrl);
        }
        throw new Error('Design FTO report export timed out');
    }

    private _getCompletedDesignExportUrl(result: ExportReportResponse): string {
        this._throwIfDesignExportFailed(result);
        if (this._isCompletedDesignExport(result)) {
            return this._requireDesignExportDownloadUrl(result);
        }
        return '';
    }

    private _throwIfDesignExportFailed(result: ExportReportResponse): void {
        if (result.status !== 'failed') return;
        throw new Error(result.error_message || 'Design FTO report export failed');
    }

    private _isCompletedDesignExport(result: ExportReportResponse): boolean {
        return result.status === 'completed' || !result.status;
    }

    private _requireDesignExportDownloadUrl(result: ExportReportResponse): string {
        if (result.download_url) return result.download_url;
        throw new Error('Design FTO report export did not return a download URL');
    }

    private _getDesignExportStatusUrl(taskId: string, result: ExportReportResponse): string {
        if (result.export_id) {
            return `/workflow/${taskId}/export?export_id=${encodeURIComponent(result.export_id)}`;
        }
        if (result.status_url) return result.status_url;
        throw new Error('Design FTO report export did not return a status URL');
    }

    private _waitForNextDesignExportPoll(): Promise<void> {
        return new Promise(resolve => window.setTimeout(resolve, DESIGN_FTO_EXPORT_POLL_INTERVAL));
    }
}
